import { useState, useEffect, useCallback, useRef } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { userService } from "../services/userService";
import {
  Search,
  Monitor,
  Smartphone,
  Tablet,
  Clock,
  MapPin,
  Shield,
  ShieldOff,
  RefreshCw,
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
  Filter,
  ChevronDown,
  Laptop,
  AlertCircle,
  AlertTriangle,
} from "lucide-react";

interface DeviceSession {
  _id: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  deviceFingerprint: string;
  browser: {
    name: string;
    version: string;
  };
  os: {
    name: string;
    version: string;
  };
  ip: string;
  deviceType: "web" | "mobile" | "tablet" | "desktop";
  location: {
    country: string;
    city: string;
  };
  isActive: boolean;
  isBlocked: boolean;
  lastActivity: string;
  createdAt: string;
}

type FilterType = "all" | "active" | "blocked" | "inactive";

export default function AuthenticationPage() {
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<DeviceSession | null>(
    null,
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Stats State
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    blocked: 0,
    inactive: 0
  });

  // Error State
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());
  const [isResetting, setIsResetting] = useState(false);

  // AbortController ref for search race condition
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filter]);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Cancel previous request to prevent race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const response = await userService.getDeviceSessions({ 
        page: currentPage, 
        limit: itemsPerPage,
        search: debouncedSearchTerm,
        status: filter
      }, abortControllerRef.current.signal);
      
      setSessions(response.sessions || response.devices || []);
      setTotalItems(response.total || 0);
      setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
      
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error("Failed to fetch sessions:", error);
      setError("Failed to fetch device sessions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filter]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleBlockDevice = async (sessionId: string) => {
    setActionLoading(prev => new Set(prev).add(sessionId));
    setError(null);
    try {
      await userService.blockDevice(sessionId);
      await fetchSessions();
    } catch (error) {
      console.error("Failed to block device:", error);
      setError("Failed to block device. Please try again.");
    } finally {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  };

  const handleUnblockDevice = async (sessionId: string) => {
    setActionLoading(prev => new Set(prev).add(sessionId));
    setError(null);
    try {
      await userService.unblockDevice(sessionId);
      await fetchSessions();
    } catch (error) {
      console.error("Failed to unblock device:", error);
      setError("Failed to unblock device. Please try again.");
    } finally {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!confirm("Are you sure you want to revoke this session?")) return;
    setActionLoading(prev => new Set(prev).add(sessionId));
    setError(null);
    try {
      await userService.revokeDeviceSession(sessionId);
      await fetchSessions();
    } catch (error) {
      console.error("Failed to revoke session:", error);
      setError("Failed to revoke session. Please try again.");
    } finally {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  };

  const handleResetAllDevices = async () => {
    if (!confirm("Are you sure you want to reset ALL devices? This will permanently delete every device record and force all users to re-register their devices on next login. This action cannot be undone.")) {
      return;
    }

    setIsResetting(true);
    setError(null);
    try {
      await userService.resetAllDevices();
      await fetchSessions();
      setError(null);
    } catch (error) {
      console.error("Failed to reset all devices:", error);
      setError("Failed to reset all devices. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case "mobile":
        return Smartphone;
      case "tablet":
        return Tablet;
      case "desktop":
        return Laptop;
      default:
        return Monitor;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Device Sessions
            </h1>
            <p className="text-gray-500 mt-1">
              Monitor and manage user device sessions
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={fetchSessions}
              className="w-full sm:w-auto"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleResetAllDevices}
              disabled={isResetting}
              className="w-full sm:w-auto border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              {isResetting ? (
                <RefreshCw size={16} className="mr-2 animate-spin" />
              ) : (
                <AlertTriangle size={16} className="mr-2" />
              )}
              Reset All
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Monitor className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total}
                </p>
                <p className="text-sm text-gray-500">Total Sessions</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.active}
                </p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <ShieldOff className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.blocked}
                </p>
                <p className="text-sm text-gray-500">Blocked</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <XCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.inactive}
                </p>
                <p className="text-sm text-gray-500">Inactive</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search by email, name, IP, or location..."
              icon={<Search size={18} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-white"
            />
          </div>
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full sm:w-auto"
            >
              <Filter size={16} className="mr-2" />
              {filter === "all"
                ? "All Sessions"
                : filter.charAt(0).toUpperCase() + filter.slice(1)}
              <ChevronDown size={16} className="ml-2" />
            </Button>
            {isFilterOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsFilterOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px]">
                  {(
                    ["all", "active", "blocked", "inactive"] as FilterType[]
                  ).map((f) => (
                    <button
                      key={f}
                      onClick={() => {
                        setFilter(f);
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50 capitalize ${
                        filter === f ? "bg-gray-100 font-medium" : ""
                      }`}
                    >
                      {f === "all" ? "All Sessions" : f}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sessions Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1000px] w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 font-semibold text-gray-900">
                    User
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900">
                    Device
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900">
                    Location
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900">
                    Last Activity
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No sessions found
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => {
                    const DeviceIcon = getDeviceIcon(session.deviceType);
                    return (
                      <tr
                        key={session._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {session.user?.firstName} {session.user?.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {session.user?.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <DeviceIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-gray-900">
                                {session.browser?.name || "Unknown"}{" "}
                                {session.browser?.version}
                              </p>
                              <p className="text-sm text-gray-500">
                                {session.os?.name} {session.os?.version}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-gray-900">
                                {session.location?.city || "Unknown"}
                                {session.location?.country &&
                                  `, ${session.location.country}`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {session.ip}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>{formatDate(session.lastActivity)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {session.isBlocked ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <ShieldOff size={12} className="mr-1" />
                              Blocked
                            </span>
                          ) : session.isActive ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle size={12} className="mr-1" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              <XCircle size={12} className="mr-1" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedSession(session);
                                setIsDetailsOpen(true);
                              }}
                              disabled={actionLoading.has(session._id)}
                              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                            {session.isBlocked ? (
                              <button
                                onClick={() => handleUnblockDevice(session._id)}
                                disabled={actionLoading.has(session._id)}
                                className="p-2 hover:bg-green-50 rounded-lg text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Unblock Device"
                              >
                                {actionLoading.has(session._id) ? (
                                  <RefreshCw size={16} className="animate-spin" />
                                ) : (
                                  <Shield size={16} />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleBlockDevice(session._id)}
                                disabled={actionLoading.has(session._id)}
                                className="p-2 hover:bg-red-50 rounded-lg text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Block Device"
                              >
                                {actionLoading.has(session._id) ? (
                                  <RefreshCw size={16} className="animate-spin" />
                                ) : (
                                  <ShieldOff size={16} />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleRevokeSession(session._id)}
                              disabled={actionLoading.has(session._id)}
                              className="p-2 hover:bg-red-50 rounded-lg text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Revoke Session"
                            >
                              {actionLoading.has(session._id) ? (
                                <RefreshCw size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
            <span>
              {totalItems > 0 ? (
                <>
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}{" "}
                  to {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} sessions
                </>
              ) : (
                "No sessions found"
              )}
            </span>
            <div className="flex gap-1 ml-4">
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &lt; Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p = i + 1;
                // Center the current page if many pages
                if (totalPages > 5) {
                  if (currentPage <= 3) {
                    p = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    p = totalPages - 4 + i;
                  } else {
                    p = currentPage - 2 + i;
                  }
                }

                if (p > totalPages) return null;

                return (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded ${
                      currentPage === p
                        ? "bg-gray-900 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={currentPage === totalPages || totalItems === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next &gt;
              </button>
            </div>
          </div>
        </Card>

        {/* Session Details Modal */}
        <Modal
          isOpen={isDetailsOpen && !!selectedSession}
          onClose={() => setIsDetailsOpen(false)}
          title="Session Details"
          className="max-w-lg"
        >
          {selectedSession && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">User</label>
                <p className="font-medium">
                  {selectedSession.user?.firstName}{" "}
                  {selectedSession.user?.lastName}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedSession.user?.email}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Browser</label>
                  <p>
                    {selectedSession.browser?.name}{" "}
                    {selectedSession.browser?.version}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">OS</label>
                  <p>
                    {selectedSession.os?.name} {selectedSession.os?.version}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">IP Address</label>
                  <p>{selectedSession.ip}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Location</label>
                  <p>
                    {selectedSession.location?.city},{" "}
                    {selectedSession.location?.country}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500">
                  Device Fingerprint
                </label>
                <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                  {selectedSession.deviceFingerprint}
                </p>
              </div>
              <div className="flex justify-end gap-2 mt-6 border-t border-gray-100 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailsOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </DashboardLayout>
  );
}
